import {
  Box,
  Button,
  ChakraProps,
  Divider,
  FormControl,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  VStack,
  useDisclosure,
  useTheme,
} from "@chakra-ui/react";
import { Dispatch, SetStateAction, useMemo, useState, useEffect } from "react";
import { DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { de } from "date-fns/locale";
import { startOfMonth, startOfYear, endOfYear, subYears } from "date-fns";
import { State } from "../types";
import { formatDate, getLocalDate } from "../utils";

const DateRangeSelector = ({
  state,
  range,
  setRange,
  ...props
}: {
  state: State;
  range: DateRange | undefined;
  setRange: Dispatch<SetStateAction<DateRange | undefined>>;
} & ChakraProps) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const theme = useTheme();
  const accentColor = theme.colors.gray[700];
  const [month, setMonth] = useState<Date>(range?.to ?? new Date());

  // Update displayed month when popover opens
  useEffect(() => {
    if (isOpen && range?.to) {
      setMonth(range.to);
    }
  }, [isOpen, range?.to]);

  const presets = useMemo(() => {
    const today = getLocalDate(new Date());
    const lastYear = subYears(today, 1);
    return [
      {
        label: "Aktueller Monat",
        range: { from: getLocalDate(startOfMonth(today)), to: today },
      },
      {
        label: "Aktuelles Jahr",
        range: { from: getLocalDate(startOfYear(today)), to: today },
      },
      {
        label: "Letztes Jahr",
        range: {
          from: getLocalDate(startOfYear(lastYear)),
          to: getLocalDate(endOfYear(lastYear)),
        },
      },
    ];
  }, []);

  const handlePresetClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    preset: DateRange
  ) => {
    e.stopPropagation();
    e.currentTarget.blur();
    setRange(preset);
    onClose();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={state !== "initial" && state !== "loading" ? onOpen : undefined}
      onClose={onClose}
      placement="bottom"
      returnFocusOnClose={false}
      {...props}
    >
      <PopoverTrigger>
        <FormControl maxWidth={{ base: "auto", md: 240 }}>
          <Input
            isReadOnly={true}
            isDisabled={state === "initial" || state === "loading"}
            cursor={
              state === "initial" || state === "loading"
                ? undefined
                : "pointer"
            }
            // Safari does not focus a readonly input on click; without focus
            // in the trigger the popover blurs shut right after opening.
            onClick={(event) => event.currentTarget.focus()}
            value={
              range && range.from && range.to
                ? `${formatDate(range.from)} - ${formatDate(range.to)}`
                : "Datum wählen"
            }
          />
        </FormControl>
      </PopoverTrigger>
      <Portal>
        <PopoverContent p={0} w="auto">
          <PopoverArrow />
          <Box bg="white" p={1} borderRadius="md">
            <VStack spacing={1} align="center">
              <DayPicker
                mode="range"
                locale={de}
                month={month}
                onMonthChange={setMonth}
                selected={range}
                onSelect={setRange}
                captionLayout="dropdown-buttons"
                fromYear={2024}
                toYear={new Date().getFullYear()}
                modifiersStyles={{
                  selected: { backgroundColor: accentColor },
                  range_start: { backgroundColor: accentColor },
                  range_end: { backgroundColor: accentColor },
                  range_middle: {
                    backgroundColor: `${accentColor}33`,
                    color: accentColor,
                  },
                }}
              />
              <Divider borderColor="gray.200" w="full" />
              <HStack spacing={0} justify="center">
                {presets.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="ghost"
                    color="gray.700"
                    size="sm"
                    fontWeight="normal"
                    onClick={(e) => handlePresetClick(e, preset.range)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </HStack>
            </VStack>
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default DateRangeSelector;
